<template>
  <DatePicker
    :modelValue="dateOf(month)"
    :inputId
    view="month"
    dateFormat="MM yy"
    :placeholder
    :ariaLabel
    :minDate="dateOf(min) ?? undefined"
    :maxDate="dateOf(max) ?? undefined"
    :manualInput="false"
    :showButtonBar="clearable"
    appendTo="body"
    :disabled
    :pt="{
      pcInputText: {
        root: {
          class: { 'is-invalid': invalid },
          'aria-invalid': invalid || undefined,
        },
      },
    }"
    :ptOptions="{ mergeProps: true }"
    @update:modelValue="month = monthOf($event)"
    @blur="$emit('blur')"
  />
</template>

<script setup lang="ts">
import DatePicker from "primevue/datepicker";

/**
 * A calendar month, picked from PrimeVue's month view. Stands in for `<input type="month">`, which
 * Firefox renders as a plain text box. The model is `YYYY-MM` — a month's period label — or `""`
 * for no month; the picker itself holds a local `Date` on the month's first day.
 *
 * The picker wraps its input, so a caller's `.invalid-feedback` is not the input's sibling and needs
 * `d-block` to show. Leaving an empty picker changes no model, so a caller that validates on touch
 * listens for `blur` as it would on a plain input.
 */
withDefaults(
  defineProps<{
    inputId?: string;
    placeholder?: string;
    ariaLabel?: string;
    /** The earliest month offered, `YYYY-MM`. */
    min?: string;
    /** The latest month offered, `YYYY-MM`. */
    max?: string;
    disabled?: boolean;
    /** Marks the input `is-invalid`; the global passthrough's classes are kept (`mergeProps`). */
    invalid?: boolean;
    /** Offers the button bar, whose Clear empties the model. */
    clearable?: boolean;
  }>(),
  {
    inputId: undefined,
    placeholder: undefined,
    ariaLabel: undefined,
    min: "",
    max: "",
    disabled: false,
    invalid: false,
    clearable: false,
  }
);

defineEmits<{
  blur: [];
}>();

const month = defineModel<string>({ required: true });

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Anything but a `YYYY-MM` month reads as no month, rather than as an Invalid Date. */
function dateOf(value: string): Date | null {
  if (!MONTH_PATTERN.test(value)) return null;
  const [year, monthNumber] = value.split("-").map(Number);
  return new Date(year, monthNumber - 1, 1);
}

function monthOf(value: unknown): string {
  if (!(value instanceof Date)) return "";
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;
}
</script>
